/**
 * Station Images API
 * API functions for the new station images feature (icon, banner, gallery)
 * Uses signed URLs for direct browser-to-GCS uploads
 */

import { getCredentials, fetchOptionsV2, baseUrlV2 } from './http';
import { FourOhOneError } from '../actionCreators/system';
import {
  IStationImagesResponse,
  IStationMediaImage,
  IUploadUrlResponse,
  IConfirmUploadResponse,
  StationImageType,
  IModerationError,
} from '../components/StationMedia/types';

/**
 * Options for fetching station images
 */
interface GetStationImagesOptions {
  includePendingReview?: boolean;
  includeRejected?: boolean;
  imageType?: StationImageType;
}

/**
 * Get all images for a station
 * GET /v6/station/{location_id}/images
 */
export const getStationMediaImages = async (
  locationId: string,
  options?: GetStationImagesOptions
): Promise<IStationImagesResponse> => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken);

  // Build query params
  const params = new URLSearchParams();
  if (options?.includePendingReview) {
    params.append('include_pending_review', 'true');
  }
  if (options?.includeRejected) {
    params.append('include_rejected', 'true');
  }
  if (options?.imageType) {
    params.append('image_type', options.imageType);
  }
  const queryString = params.toString();
  const url = `${baseUrlV2}/station/${locationId}/images${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, fetchOps);
  const result = await response.json();

  if (result.data) {
    return result.data;
  }

  if (response.status === 404) {
    // Station has no images yet
    return {
      icon: null,
      banner: null,
      gallery: [],
    };
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to fetch station images');
};

/**
 * Request a signed upload URL for direct GCS upload
 * POST /v6/station/{location_id}/images/upload-url
 */
export const getStationImageUploadUrl = async (
  locationId: string,
  imageType: StationImageType,
  contentType: string,
  filename?: string,
  uploadedBy?: string
): Promise<IUploadUrlResponse> => {
  const { adminEmail, adminToken } = getCredentials();
  const body = {
    image_type: imageType,
    content_type: contentType,
    ...(filename && { filename }),
    ...(uploadedBy && { uploaded_by: uploadedBy }),
  };
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'POST', body as any);

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/upload-url`,
    fetchOps
  );
  const result = await response.json();

  if (result.data) {
    return result.data;
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to get upload URL');
};

/**
 * Upload file directly to GCS using signed URL
 * Uses XMLHttpRequest for progress tracking
 */
export const uploadToSignedUrl = (
  signedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

/**
 * Confirm upload and trigger AI moderation
 * POST /v6/station/{location_id}/images/confirm
 */
export const confirmStationImageUpload = async (
  locationId: string,
  imageId: string
): Promise<IConfirmUploadResponse> => {
  const { adminEmail, adminToken } = getCredentials();
  const body = { image_id: imageId };
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'POST', body as any);

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/confirm`,
    fetchOps
  );
  const result = await response.json();

  if (result.data) {
    return result.data;
  }

  // Handle moderation failure
  if (result.error === 'content_moderation_failed') {
    const moderationError: IModerationError = result;
    throw new ModerationError(
      moderationError.message,
      moderationError.details?.reason || 'Content policy violation'
    );
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to confirm upload');
};

/**
 * Delete a station image
 * DELETE /v6/station/{location_id}/images/{image_id}
 */
export const deleteStationImage = async (
  locationId: string,
  imageId: string
): Promise<void> => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'DELETE');

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/${imageId}`,
    fetchOps
  );

  if (response.status === 200 || response.status === 204) {
    return;
  }

  const result = await response.json();
  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to delete image');
};

/**
 * Reorder gallery images
 * PUT /v2/station/{location_id}/images/gallery/reorder
 */
export const reorderStationGallery = async (
  locationId: string,
  imageIds: string[]
): Promise<IStationMediaImage[]> => {
  const { adminEmail, adminToken } = getCredentials();
  const body = { image_ids: imageIds };
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'PUT', body as any);

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/gallery/reorder`,
    fetchOps
  );
  const result = await response.json();

  if (result.data) {
    return result.data.gallery;
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to reorder gallery');
};

/**
 * Approve an image that is pending manual review
 * POST /v6/station/{location_id}/images/{image_id}/approve
 */
export const approveStationImage = async (
  locationId: string,
  imageId: string
): Promise<{ message: string; image_id: string; status: string }> => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'POST');

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/${imageId}/approve`,
    fetchOps
  );
  const result = await response.json();

  if (result.data) {
    return result.data;
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to approve image');
};

/**
 * Reject an image that is pending manual review
 * POST /v6/station/{location_id}/images/{image_id}/reject
 */
export const rejectStationImage = async (
  locationId: string,
  imageId: string
): Promise<{ message: string; image_id: string }> => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken, 'POST');

  const response = await fetch(
    `${baseUrlV2}/station/${locationId}/images/${imageId}/reject`,
    fetchOps
  );
  const result = await response.json();

  if (result.data) {
    return result.data;
  }

  FourOhOneError(result);
  throw new Error(result.error_description || 'Failed to reject image');
};

/**
 * Custom error class for moderation failures
 */
export class ModerationError extends Error {
  public reason: string;

  constructor(message: string, reason: string) {
    super(message);
    this.name = 'ModerationError';
    this.reason = reason;
  }
}

/**
 * Helper to check if an error is a moderation error
 */
export const isModerationError = (error: unknown): error is ModerationError => {
  return error instanceof ModerationError;
};
